import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './ChecklistTemplate.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialChecklistTemplateState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import bs from '../../styles/bootstrap-overrides.scss';
import TaskTemplates from './TaskTemplates';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import OperationalSetupContentTemplate from '../OperationalSetup/OperationalSetupContentTemplate';
import classNames from 'classnames';
import { isRequired, getQuerystring } from '../../utils';
import Cookies from 'js-cookie';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import Checkbox from '../WSAControls/CheckboxContainer/Checkbox';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const ChecklistTemplate = ({
  intl,
  userProfileWidgets,
  checklistTemplate =  {
    ...initialChecklistTemplateState.checklistTemplate
  },
  describeChecklistTemplate,
  saveChecklistTemplate,
  changeChecklistTemplate
}) => {
  const history = useNavigate();

  let IsEdit = false;
  let IsView = false;

  let { checklistTemplateId } = useParams();
  let view = getQuerystring('view');

  const [disableTaskTemplate, setDisableTaskTemplate] = useState(false);
  const [formError, setFormError] = useState({});
  const [searchKeywords, setSearchKeywords] = useState('');
  const [showDialog, setShowDialog] = useState(false);

  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  checklistTemplateId = parseInt(checklistTemplateId);
  const dialogTitle = intl.formatMessage({
    id: 'checklistTemplateManagement.confirmServiceTaskTemplate',
    defaultMessage: 'Tasks attached to the checklist will be converted to service tasks. Do you want to continue?'
  });

  IsEdit = checklistTemplateId !== 0 && !view;
  IsView = checklistTemplateId !== 0 && view === '1';

  if (IsEdit && !IsWidgetAccessible(userProfileWidgets, 'CHECKLISTTEMPLATEEDIT')) {
    history('/unauth');
  }

  if (IsView && !IsWidgetAccessible(userProfileWidgets, 'CHECKLISTTEMPLATEVIEW')) {
    history('/unauth');
  }

  if (checklistTemplateId === 0 && !IsWidgetAccessible(userProfileWidgets, 'CHECKLISTTEMPLATEADDNEW')) {
    history('/unauth');
  }

  let widgetCode = '';

  if (IsEdit) {
    widgetCode = 'CHECKLISTTEMPLATEEDIT';
  } else if (IsView) {
    widgetCode = 'CHECKLISTTEMPLATEVIEW';
  } else {
    widgetCode = 'CHECKLISTTEMPLATEADDNEW';
  }

  useEffect(() => {
    changeChecklistTemplate({
      SelectedChecklistTemplate: {
        ...initialChecklistTemplateState.checklistTemplate.SelectedChecklistTemplate,
        SelectedTaskTemplates: []
      }
    });
    describeChecklistTemplate(checklistTemplateId, widgetCode);
  }, [describeChecklistTemplate, changeChecklistTemplate, checklistTemplateId, IsView]);

  //Saving
  const submitForm = e => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = checklistTemplate.SelectedChecklistTemplate;
    saveData.IsEdit = IsEdit;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 1;

    if (!IsEdit) {
      saveData.TaskTemplateId = 0;
      saveData.GuidIdentifier = uuidv4();
    }

    saveChecklistTemplate(saveData, widgetCode);
  };

  const validateInput = () => {
    let formError = {};

    let isValid = true;

    if (
      !checklistTemplate ||
      !checklistTemplate.SelectedChecklistTemplate ||
      !checklistTemplate.SelectedChecklistTemplate.Name ||
      isRequired(checklistTemplate.SelectedChecklistTemplate.Name, 1)
    ) {
      formError.Name = intl.formatMessage({
        id: 'checklistTemplateManagement.nameMandatory',
        defaultMessage: 'Checklist Name is a mandatory field'
      });
      isValid = false;
    }

    if (
      !checklistTemplate ||
      !checklistTemplate.SelectedChecklistTemplate ||
      !checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates ||
      checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates.length === 0
    ) {
      formError.Task = intl.formatMessage({
        id: 'checklistTemplateManagement.taskTemplateMandatory',
        defaultMessage: 'Task Tamplate is a mandatory field'
      });
      isValid = false;
    }
    setFormError(formError);

    return isValid;
  };

  //on control value change
  const onChange = e => {
    if (e.target && e.target.type && e.target.type === 'checkbox') {
      checklistTemplate.SelectedChecklistTemplate = {
        ...checklistTemplate.SelectedChecklistTemplate,
        [e.target.name]: e.target.checked
      };
      if (e.target.checked && checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates.length > 0) {
        setShowDialog(true);
      }
    } else {
      checklistTemplate.SelectedChecklistTemplate = {
        ...checklistTemplate.SelectedChecklistTemplate,
        [e.target.name]: e.target.value
      };
    }
    changeChecklistTemplate(checklistTemplate);
  };

  const onTaskAdd = taskTemplateId => {
    let selectedTask = checklistTemplate.TaskTemplates.filter(e => e.TaskTemplateId === taskTemplateId);
    if (selectedTask && selectedTask.length > 0) {
      selectedTask[0].UpdatedBy = Cookies.get(`userid-${envName}`) || 1;
      checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates.push(selectedTask[0]);
      changeChecklistTemplate(checklistTemplate);
    }
  };

  const onTaskSearch = keyWords => {
    setSearchKeywords(keyWords);
  };

  const getSelectedTaskTemplates = () => {
    return checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates.filter(
      task => task.Name.toLowerCase().indexOf(searchKeywords.toLowerCase()) >= 0
    );
  };

  const onTaskRemove = taskTemplateId => {
    let selectedTasks = checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates.filter(e => e.TaskTemplateId !== taskTemplateId);
    checklistTemplate.SelectedChecklistTemplate.SelectedTaskTemplates = selectedTasks;
    changeChecklistTemplate(checklistTemplate);
  };

  const onAddNewOrCancelClick = status => {
    setDisableTaskTemplate(status);
  };

  let messageId = (checklistTemplate && checklistTemplate.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const checklistName = (checklistTemplate.SelectedChecklistTemplate ? checklistTemplate.SelectedChecklistTemplate.Name : '') || '';
  let heading = '';
  if (IsEdit) {
    heading = intl.formatMessage({ id: 'checklistTemplate.edit', defaultMessage: 'Edit' }) + ' ' + checklistName;
  } else if (IsView) {
    heading = checklistName + ' ' + intl.formatMessage({ id: 'checklistTemplate.checklistTemplate', defaultMessage: 'Checklist Template' });
  } else {
    heading = intl.formatMessage({ id: 'checklistTemplate.newChecklistTemplate', defaultMessage: 'New Checklist Template' });
  }

  const backLink = !IsEdit
    ? '/operational-setup/checklist-task-templates'
    : `/admin-controls/checklist-template/${checklistTemplateId}?view=1`;

  const redirect = () => {
    if (checklistTemplate.isOpSuccessful && !checklistTemplate.showBanner) {
      changeChecklistTemplate({
        SelectedChecklistTemplate: {
          ...initialChecklistTemplateState.checklistTemplate.SelectedChecklistTemplate,
          SelectedTaskTemplates: []
        }
      });
      return <Navigate to={backLink} />;
    }
  };

  const handleConfirm = () => {
    setShowDialog(false);
  };

  const handleClose = () => {
    setShowDialog(false);
    checklistTemplate.SelectedChecklistTemplate = {
      ...checklistTemplate.SelectedChecklistTemplate,
      IsServiceChecklist: false
    };
    changeChecklistTemplate(checklistTemplate);
  };

  return (
    <div className={s.checklistTemplate}>
      {checklistTemplate.isLoading && <LoadingSpinner />}
      {redirect()}
      <Banner
        key={uuidv4()}
        failureText={messageText}
        showBanner={checklistTemplate.showBanner}
        status={checklistTemplate.isOpSuccessful}
        successText={messageText}
      />

      <OperationalSetupContentTemplate selectedPage="checklistTaskTemplate" userProfileWidgets={userProfileWidgets}>
        <div className={s.contentWrapper}>
          <div className={s.checklistTemplateHeader}>
            <Link className={s.backLink} to={backLink}>
              &lt; &nbsp;
              <FormattedMessage id="checklistTemplateManagement.back" defaultMessage="BACK" />
            </Link>
            <Row>
              <Col>
                <h3 data-unittest="headingLabel">{heading}</h3>
              </Col>
              <Col className={s.colChangeButton}>
                {IsView && (
                  <Link
                    to={`/admin-controls/checklist-template/${checklistTemplateId}`}
                    style={WidgetVisibility(userProfileWidgets, `CHECKLISTTEMPLATEEDIT`)}
                  >
                    <Button
                      variant="primary"
                      data-unittest="saveData"
                      className={classNames(s.margin5, s.btnSaveChanges)}
                      noValidate
                      disabled={disableTaskTemplate}
                    >
                      <FormattedMessage id="common.edit" defaultMessage="EDIT" />
                    </Button>
                  </Link>
                )}
              </Col>
            </Row>
          </div>
          <div className={s.checklistTemplateContent}>
            <h4>
              <FormattedMessage id="checklistTemplate.details" defaultMessage="Details" />
            </h4>
            <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={showDialog} />
            {/* EDIT SCREEN START */}
            {!IsView && (
              <Form className={s.viewRow}>
                <Row>
                  <Col xs={12} lg={6}>
                    <Form.Group controlId="formChecklistTemplateName">
                      <Form.Label>
                        <FormattedMessage id="checklistTemplateManagement.name" defaultMessage="Name" />
                      </Form.Label>

                      <Form.Control
                        type="text"
                        name="Name"
                        onChange={onChange}
                        value={checklistTemplate.SelectedChecklistTemplate.Name}
                        className={`${s.formControl} ${formError && formError.Name ? s.formControlError : ''}`}
                        placeholder={intl.formatMessage({
                          id: 'checklistTemplateManagement.name',
                          defaultMessage: 'Name'
                        })}
                      />
                      {formError && formError.Name && (
                        <p role="alert" className={s.error}>
                          {formError.Name}
                        </p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId="formChecklistTemplateDescription">
                      <Form.Label>
                        <FormattedMessage id="checklistTemplateManagement.description" defaultMessage="Description" />
                      </Form.Label>

                      <Form.Control
                        className={s.txtArea}
                        as="textarea"
                        rows="3"
                        name="Description"
                        onChange={onChange}
                        placeholder={intl.formatMessage({
                          id: 'checklistTemplateManagement.description',
                          defaultMessage: 'Description'
                        })}
                        value={checklistTemplate.SelectedChecklistTemplate.Description}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId="formServiceChecklistTemplate">
                      <Checkbox
                        key={uuidv4()}
                        dataArray={[
                          {
                            SKEY: 'IsServiceChecklist',
                            target: { type: 'checkbox' },
                            label: intl.formatMessage({
                              id: 'checklistTemplateManagement.serviceChecklist',
                              defaultMessage: 'Service checklist'
                            }),
                            disable: checklistTemplate.SelectedChecklistTemplate.ModelServicePlanCount > 0,
                            isChecked: checklistTemplate.SelectedChecklistTemplate.IsServiceChecklist
                          }
                        ]}
                        onSelectionChange={onChange}
                      />
                      {checklistTemplate.SelectedChecklistTemplate.ModelServicePlanCount > 0 ? (
                        <p className={s.serviceChecklistInfo}>
                          <FormattedMessage
                            id="checklistTemplateManagement.serviceChecklistTemplateInUse"
                            defaultMessage="Checklist template used in model variant service plan must be service checklist template. "
                          />
                        </p>
                      ) : (
                        ''
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            )}
            {/* EDIT SCREEN END */}

            {/* VIEW SCREEN START */}
            {IsView && (
              <Form>
                <Row className={s.viewRow}>
                  <Col xs={12} lg={3}>
                    <Form.Label>
                      <FormattedMessage id="checklistTemplateManagement.name" defaultMessage="Name" />
                    </Form.Label>
                  </Col>
                  <Col>{checklistTemplate.SelectedChecklistTemplate.Name}</Col>
                </Row>
                <Row className={s.viewRow}>
                  <Col xs={12} lg={3}>
                    <Form.Group controlId="formChecklistTemplateDescription">
                      <Form.Label>
                        <FormattedMessage id="checklistTemplateManagement.description" defaultMessage="Description" />
                      </Form.Label>
                    </Form.Group>
                  </Col>
                  <Col>{checklistTemplate.SelectedChecklistTemplate.Description}</Col>
                </Row>
                <Row className={s.viewRow}>
                  <Col xs={12} lg={3}>
                    <Form.Group controlId="formServiceChecklist">
                      <Form.Label>
                        <FormattedMessage id="checklistTemplateManagement.serviceChecklist" defaultMessage="Service checklist" />
                      </Form.Label>
                    </Form.Group>
                  </Col>
                  <Col>{checklistTemplate.SelectedChecklistTemplate.IsServiceChecklist ? 'Yes' : 'No'}</Col>
                </Row>
              </Form>
            )}
            {/* VIEW SCREEN END */}
          </div>
          <div>
            <TaskTemplates
              onTaskSearch={onTaskSearch}
              onTaskAdd={onTaskAdd}
              onTaskRemove={onTaskRemove}
              onAddNewOrCancelClick={onAddNewOrCancelClick}
              selectedTaskTemplates={getSelectedTaskTemplates() || []}
              taskTemplates={checklistTemplate.TaskTemplates}
              isView={IsView}
              isEdit={IsEdit}
              isServiceChecklist={checklistTemplate.SelectedChecklistTemplate.IsServiceChecklist}
              checklistTemplateId={checklistTemplateId}
            />
          </div>
          {formError && formError.Task && (
            <p role="alert" className={s.error}>
              {formError.Task}
            </p>
          )}
          <div>
            {!IsView && (
              <Form>
                <Button
                  variant="primary"
                  data-unittest="saveData"
                  className={classNames(s.margin5, s.btnSaveChanges)}
                  onClick={submitForm}
                  noValidate
                  disabled={disableTaskTemplate}
                >
                  <FormattedMessage id="checklistTemplateManagement.saveChanges" defaultMessage="Save Changes" />
                </Button>

                <Link to={backLink}>
                  <Button disabled={disableTaskTemplate} variant="outline-secondary" className={s.btnCancel}>
                    <FormattedMessage id="checklistTemplateManagement.cancel" defaultMessage="Cancel" />
                  </Button>
                </Link>
              </Form>
            )}
          </div>
        </div>
      </OperationalSetupContentTemplate>
    </div>
  );
};


ChecklistTemplate.propTypes = {
  saveChecklistTemplate: PropTypes.func.isRequired,
  checklistTemplate: PropTypes.object.isRequired,
  describeChecklistTemplate: PropTypes.func.isRequired,
  changeChecklistTemplate: PropTypes.func.isRequired
};

export default injectIntl(ChecklistTemplate);
